import { useState, useEffect, useRef, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useEnhancedGameSession } from '../hooks/useEnhancedGameSession'
import { GameHeader } from '../components/GameHeader'
import { sounds } from '../utils/sounds'
import { UserProfileManager } from '../utils/userProfile'
import '../styles/GamePage.css'
import '../styles/Pictionary.css'
import '../styles/UniversalGameRoom.css'
import '../styles/mobile-fixes.css'
import '../styles/mobile-bottom-sheet.css'
import { GameRoomSidebar } from '../components/GameRoom/GameRoomSidebar'
import type { Message, HistoryItem } from '../components/GameRoom/GameRoomSidebar'
import { useSidebar } from '../hooks/useSidebar'

type Phase = 'setup' | 'lobby' | 'word_select' | 'drawing' | 'reveal' | 'finished'

interface GameState {
  phase: Phase
  round: number
  currentDrawer: number
  word?: string
  wordOptions?: string[]
  guesses: Array<{ playerId: number; playerName: string; guess: string; correct: boolean }>
  scores: Record<number, number>
  totalRounds: number
  totalPlayers: number
  timeLeft: number
  canvasData?: string // Base64 encoded canvas
}

const WORD_LISTS = {
  easy: ['cat', 'dog', 'house', 'tree', 'car', 'sun', 'moon', 'star', 'fish', 'bird', 'book', 'chair', 'cup', 'ball', 'flower'],
  medium: ['pizza', 'guitar', 'camera', 'laptop', 'umbrella', 'bicycle', 'football', 'glasses', 'backpack', 'lighthouse', 'rainbow', 'mountain', 'rocket', 'castle', 'bridge'],
  hard: ['astronaut', 'telescope', 'volcano', 'parachute', 'hammock', 'cactus', 'tornado', 'skateboard', 'peacock', 'pyramid', 'microscope', 'submarine', 'kangaroo', 'saxophone', 'dinosaur'],
}

const ALL_WORDS = [...WORD_LISTS.easy, ...WORD_LISTS.medium, ...WORD_LISTS.hard]

export default function Pictionary() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const autoJoinCode = searchParams.get('join')

  // Auto-fill from profile
  const userProfile = UserProfileManager.get()

  const [playerName, setPlayerName] = useState(userProfile?.name || '')
  const [playerNumber, setPlayerNumber] = useState<1 | 2>(1)
  const [setupPhase, setSetupPhase] = useState<'select' | 'create' | 'join'>('select')
  const [numPlayers, setNumPlayers] = useState(2)
  const [rounds, setRounds] = useState(3)

  const enhancedSession = useEnhancedGameSession({
    sessionCode: undefined,
    playerNumber,
    playerName,
  })

  const { session, createSession, joinSession, voice,  sendMove, onMessage } = enhancedSession

  const [gameState, setGameState] = useState<GameState>({
    phase: 'setup',
    round: 1,
    currentDrawer: 1,
    guesses: [],
    scores: {},
    totalRounds: 3,
    totalPlayers: 2,
    timeLeft: 60,
  })

  const [currentGuess, setCurrentGuess] = useState('')

  // Canvas refs
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [color, setColor] = useState('#000000')
  const [brushSize, setBrushSize] = useState(3)

  // Universal sidebar state
  const [chatMessages, setChatMessages] = useState<Message[]>([])
  const [historyItems] = useState<HistoryItem[]>([])
  const sidebar = useSidebar()

  // Sync game state from host
  useEffect(() => {
    const unsubscribe = onMessage('game_state', (data) => {
      console.log('[Pictionary] Received game state:', data)
      setGameState(data.state)

      // Update canvas if we're not the drawer
      if (data.state.canvasData && data.state.currentDrawer !== playerNumber) {
        const canvas = canvasRef.current
        if (canvas) {
          const ctx = canvas.getContext('2d')
          if (ctx) {
            const img = new Image()
            img.onload = () => {
              ctx.clearRect(0, 0, canvas.width, canvas.height)
              ctx.drawImage(img, 0, 0)
            }
            img.src = data.state.canvasData
          }
        }
      }
    })

    return () => unsubscribe()
  }, [onMessage, playerNumber])

  // Handle chat messages
  useEffect(() => {
    const unsubscribeChat = onMessage('chat', (data) => {
      console.log('[Pictionary] Chat message:', data.message)
      const newMessage: Message = {
        sender: data.playerNumber === playerNumber ? 'you' : 'opponent',
        content: data.message,
        timestamp: Date.now()
      }
      setChatMessages(prev => [...prev, newMessage])
    })

    return () => {
      unsubscribeChat()
    }
  }, [onMessage, playerNumber])

  // Handler for sidebar message sending
  const handleSidebarMessage = useCallback(async (message: string) => {
    await sendMove({ type: 'chat', message })
  }, [sendMove])

  // Timer countdown (host only)
  useEffect(() => {
    if (playerNumber !== 1) return
    if (gameState.phase !== 'drawing') return
    if (gameState.timeLeft <= 0) return

    const interval = setInterval(() => {
      const newTimeLeft = gameState.timeLeft - 1

      if (newTimeLeft <= 0) {
        endRound()
      } else {
        const newState = { ...gameState, timeLeft: newTimeLeft }
        setGameState(newState)
        sendMove({ type: 'game_state', state: newState })
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [gameState, playerNumber, sendMove])

  const getRandomWords = (count: number): string[] => {
    const shuffled = [...ALL_WORDS].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, count)
  }

  // Start game
  const startGame = () => {
    if (playerNumber !== 1) {
      alert('Only the host can start the game!')
      return
    }

    const newState: GameState = {
      phase: 'word_select',
      round: 1,
      currentDrawer: 1,
      wordOptions: getRandomWords(3),
      guesses: [],
      scores: {},
      totalRounds: rounds,
      totalPlayers: numPlayers,
      timeLeft: 60,
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.success()
  }

  // Select word to draw
  const selectWord = (word: string) => {
    if (playerNumber !== gameState.currentDrawer) return

    const newState: GameState = {
      ...gameState,
      word,
      wordOptions: undefined,
      phase: 'drawing',
      timeLeft: 60,
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
  }

  // Canvas drawing with touch support
  const getPointerPos = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }

    const rect = canvas.getBoundingClientRect()

    if ('touches' in e) {
      // Touch event
      const touch = e.touches[0] || e.changedTouches[0]
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top,
      }
    } else {
      // Mouse event
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
    }
  }

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (gameState.currentDrawer !== playerNumber) return
    if (gameState.phase !== 'drawing') return

    e.preventDefault()
    setIsDrawing(true)
    draw(e, true)
  }

  const stopDrawing = () => {
    setIsDrawing(false)
  }

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>, isStart = false) => {
    if (!isDrawing && !isStart) return
    if (gameState.currentDrawer !== playerNumber) return
    if (gameState.phase !== 'drawing') return

    e.preventDefault()

    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const pos = getPointerPos(e)

    ctx.strokeStyle = color
    ctx.lineWidth = brushSize
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'

    if (isStart) {
      ctx.beginPath()
      ctx.moveTo(pos.x, pos.y)
    } else {
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
    }

    // Throttle canvas updates (send every 100ms max)
    if (!(window as any).canvasThrottle) {
      (window as any).canvasThrottle = true
      setTimeout(() => {
        const canvasData = canvas.toDataURL()
        const newState = { ...gameState, canvasData }
        setGameState(newState)
        sendMove({ type: 'game_state', state: newState })
        ;(window as any).canvasThrottle = false
      }, 100)
    }
  }

  const clearCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const newState = { ...gameState, canvasData: '' }
    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
    sounds.click()
  }

  // Submit guess
  const submitGuess = () => {
    if (!currentGuess.trim()) return
    if (gameState.currentDrawer === playerNumber) return
    if (gameState.phase !== 'drawing') return

    const correct = currentGuess.toLowerCase().trim() === gameState.word?.toLowerCase()

    const newGuesses = [
      ...gameState.guesses,
      { playerId: playerNumber, playerName, guess: currentGuess, correct },
    ]

    if (correct) {
      // Award points
      const newScores = { ...gameState.scores }
      newScores[playerNumber] = (newScores[playerNumber] || 0) + 10
      newScores[gameState.currentDrawer] = (newScores[gameState.currentDrawer] || 0) + 5

      const newState: GameState = {
        ...gameState,
        guesses: newGuesses,
        scores: newScores,
        phase: 'reveal',
      }

      setGameState(newState)
      sendMove({ type: 'game_state', state: newState })
      sounds.success()

      // Auto-advance after 3 seconds
      if (playerNumber === 1) {
        setTimeout(() => nextRound(), 3000)
      }
    } else {
      const newState = { ...gameState, guesses: newGuesses }
      setGameState(newState)
      sendMove({ type: 'game_state', state: newState })
    }

    setCurrentGuess('')
    sounds.click()
  }

  const endRound = () => {
    if (playerNumber !== 1) return

    const newState: GameState = {
      ...gameState,
      phase: 'reveal',
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })

    // Auto-advance after 5 seconds
    setTimeout(() => nextRound(), 5000)
  }

  const nextRound = () => {
    if (playerNumber !== 1) return

    const nextDrawer = (gameState.currentDrawer % gameState.totalPlayers) + 1
    const nextRoundNum = gameState.round + 1

    if (nextRoundNum > gameState.totalRounds) {
      // Game finished
      const newState: GameState = {
        ...gameState,
        phase: 'finished',
      }
      setGameState(newState)
      sendMove({ type: 'game_state', state: newState })
      return
    }

    clearCanvas()

    const newState: GameState = {
      phase: 'word_select',
      round: nextRoundNum,
      currentDrawer: nextDrawer,
      wordOptions: getRandomWords(3),
      guesses: [],
      scores: gameState.scores,
      totalRounds: gameState.totalRounds,
      totalPlayers: gameState.totalPlayers,
      timeLeft: 60,
      canvasData: '',
    }

    setGameState(newState)
    sendMove({ type: 'game_state', state: newState })
  }

  // Auto-join
  useEffect(() => {
    if (autoJoinCode && setupPhase === 'select') {
      setSetupPhase('join')
    }
  }, [autoJoinCode, setupPhase])

  // Setup screens
  if (setupPhase === 'select') {
    return (
      <div className="game-container">
        <h1>🎨 Pictionary</h1>
        <p>Draw and guess - test your artistic skills!</p>
        <div className="mode-selection">
          <button onClick={() => setSetupPhase('create')}>Create Game Room</button>
          <button onClick={() => setSetupPhase('join')}>Join Game</button>
          <button onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    )
  }

  if (setupPhase === 'create') {
    return (
      <div className="game-container">
        <h1>🎨 Pictionary</h1>
        <div className="game-setup">
          <input
            type="text"
            placeholder="Enter your name"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            maxLength={20}
          />
          <div className="setup-options">
            <label>
              Number of Players:
              <select value={numPlayers} onChange={(e) => setNumPlayers(Number(e.target.value))}>
                <option value={2}>2 Players</option>
                <option value={3}>3 Players</option>
                <option value={4}>4 Players</option>
                <option value={5}>5 Players</option>
                <option value={6}>6 Players</option>
              </select>
            </label>
            <label>
              Number of Rounds:
              <input
                type="number"
                min="1"
                max="10"
                value={rounds}
                onChange={(e) => setRounds(Number(e.target.value))}
              />
            </label>
          </div>
          <button
            onClick={async () => {
              if (!playerName) return
              await createSession('pictionary', playerName)
              setPlayerNumber(1)
              setGameState({ ...gameState, phase: 'lobby', totalRounds: rounds, totalPlayers: numPlayers })
            }}
            disabled={!playerName}
          >
            Create Game Room
          </button>
          <button onClick={() => setSetupPhase('select')}>Back</button>
        </div>
      </div>
    )
  }

  if (setupPhase === 'join') {
    return (
      <div className="game-container">
        <h1>🎨 Pictionary</h1>
        <div className="game-setup">
          <input
            type="text"
            placeholder="Enter your name"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            maxLength={20}
          />
          <input
            type="text"
            placeholder="Enter game code"
            defaultValue={autoJoinCode || ''}
            id="join-code"
          />
          <button
            onClick={async () => {
              const code = (document.getElementById('join-code') as HTMLInputElement).value
              if (!playerName || !code) return
              await joinSession(code, playerName)
              setPlayerNumber(2) // Will be updated by game state
              setGameState({ ...gameState, phase: 'lobby' })
            }}
            disabled={!playerName}
          >
            Join Game
          </button>
          <button onClick={() => setSetupPhase('select')}>Back</button>
        </div>
      </div>
    )
  }

  // Lobby
  if (gameState.phase === 'lobby') {
    return (
      <div className="pictionary-container">
        <GameHeader
          gameTitle="🎨 Pictionary"
          players={[
            { number: 1, name: session?.player1_name || 'Player 1' },
            { number: 2, name: session?.player2_name || 'Player 2' },
          ].slice(0, gameState.totalPlayers)}
          sessionCode={session?.session_code}
          voiceControls={{
            connectionStatus: voice.connectionStatus,
            voiceStatus: voice.voiceStatus,
            onToggleMute: voice.toggleMute,
            onConnect: voice.connect,
            onDisconnect: voice.disconnect,
          }}
        />

        <div className="lobby-container">
          <h2>Waiting for players...</h2>
          <div className="session-info">
            <p><strong>Session Code:</strong> {session?.session_code}</p>
            <p><strong>Players Needed:</strong> {gameState.totalPlayers}</p>
            <p><strong>Rounds:</strong> {gameState.totalRounds}</p>
            <p><strong>Join URL:</strong></p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <code style={{ flex: '1', minWidth: '200px' }}>
                {window.location.origin}/pictionary?join={session?.session_code}
              </code>
              <button
                onClick={() => {
                  const url = `${window.location.origin}/pictionary?join=${session?.session_code}`
                  navigator.clipboard.writeText(url)
                  sounds.click()
                }}
                style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
              >
                📋 Copy URL
              </button>
            </div>
          </div>

          <div className="player-requirements">
            <p>⚠️ Make sure all {gameState.totalPlayers} players join before starting!</p>
          </div>

          {playerNumber === 1 && (
            <button className="start-button" onClick={startGame}>
              Start Game
            </button>
          )}

          {playerNumber !== 1 && (
            <p className="waiting-text">Waiting for host to start...</p>
          )}
        </div>
      </div>
    )
  }

  // Main game
  const isDrawer = gameState.currentDrawer === playerNumber
  const sortedScores = Object.entries(gameState.scores).sort((a, b) => b[1] - a[1])

  return (
    <div className="pictionary-container universal-game-room">
      <GameHeader
        gameTitle="🎨 Pictionary"
        players={Object.keys(gameState.scores).map(pId => ({
          number: parseInt(pId),
          name: `Player ${pId}`,
          isConnected: true,
        }))}
        sessionCode={session?.session_code}
        voiceControls={{
          connectionStatus: voice.connectionStatus,
          voiceStatus: voice.voiceStatus,
          onToggleMute: voice.toggleMute,
          onConnect: voice.connect,
          onDisconnect: voice.disconnect,
        }}
      />

      <div className="game-info" style={{ marginTop: '60px', padding: 'var(--space-md)', textAlign: 'center' }}>
        <span>Round {gameState.round}/{gameState.totalRounds}</span>
        <span className="timer" style={{ marginLeft: '1rem' }}>{gameState.timeLeft}s</span>
        {voice.isConnected && <span className="webrtc-badge" style={{ marginLeft: '1rem' }}>⚡ Real-time</span>}
      </div>

      {gameState.phase === 'finished' && (
        <div className="winner-overlay">
          <h2>🎉 Game Finished!</h2>
          <div className="final-scores">
            <h3>Final Scores</h3>
            {sortedScores.map(([playerId, score]) => (
              <div key={playerId} className="score-entry">
                Player {playerId}: {score} points
              </div>
            ))}
            {sortedScores.length > 0 && (
              <div className="winner-announce">
                🏆 Winner: Player {sortedScores[0][0]} with {sortedScores[0][1]} points!
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main content area with game board and sidebar */}
      <div className="game-room-content">
        {/* Game Board */}
        <div className="game-board-area">
        {/* Word selection */}
        {gameState.phase === 'word_select' && isDrawer && gameState.wordOptions && (
          <div className="word-selection">
            <h3>Choose a word to draw:</h3>
            <div className="word-options">
              {gameState.wordOptions.map(word => (
                <button key={word} onClick={() => selectWord(word)} className="word-button">
                  {word}
                </button>
              ))}
            </div>
          </div>
        )}

        {gameState.phase === 'word_select' && !isDrawer && (
          <div className="waiting-message">
            <h3>Player {gameState.currentDrawer} is choosing a word...</h3>
          </div>
        )}

        {/* Drawing area */}
        {(gameState.phase === 'drawing' || gameState.phase === 'reveal') && (
          <div className="canvas-area">
            <div className="word-display">
              {isDrawer || gameState.phase === 'reveal' ? (
                <h2>Word: {gameState.word}</h2>
              ) : (
                <h2>Guess the drawing!</h2>
              )}
            </div>

            {isDrawer && gameState.phase === 'drawing' && (
              <div className="drawing-tools">
                <label>
                  Color:
                  <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
                </label>
                <label>
                  Size:
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={brushSize}
                    onChange={(e) => setBrushSize(Number(e.target.value))}
                  />
                  <span>{brushSize}px</span>
                </label>
                <button onClick={clearCanvas} className="clear-button">Clear Canvas</button>
              </div>
            )}

            <canvas
              ref={canvasRef}
              width={800}
              height={600}
              className="drawing-canvas"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>
        )}

        {/* Guessing area */}
        {gameState.phase === 'drawing' && !isDrawer && (
          <div className="guess-area">
            <input
              type="text"
              placeholder="Type your guess..."
              value={currentGuess}
              onChange={(e) => setCurrentGuess(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && submitGuess()}
              autoFocus
            />
            <button onClick={submitGuess} disabled={!currentGuess.trim()}>
              Submit Guess
            </button>
          </div>
        )}

        {/* Guesses list */}
        {gameState.guesses.length > 0 && (
          <div className="guesses-list">
            <h3>Guesses</h3>
            <div className="guesses-scroll">
              {gameState.guesses.map((g, i) => (
                <div key={i} className={`guess-entry ${g.correct ? 'correct' : ''}`}>
                  <strong>{g.playerName}:</strong> {g.guess}
                  {g.correct && ' ✓ CORRECT!'}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Scoreboard */}
        <div className="scoreboard">
          <h3>Scores</h3>
          {sortedScores.length > 0 ? (
            sortedScores.map(([playerId, score]) => (
              <div key={playerId} className="score-entry">
                <span>Player {playerId}</span>
                <span>{score} pts</span>
              </div>
            ))
          ) : (
            <p>No scores yet</p>
          )}
        </div>

        <div className="game-rules">
          <details>
            <summary>📖 How to Play</summary>
            <ul>
              <li>One player draws a word while others guess</li>
              <li>Drawer chooses from 3 random words</li>
              <li>Type your guesses - first correct guess wins!</li>
              <li>Correct guess: +10 points for guesser, +5 for drawer</li>
              <li>60 seconds per round</li>
              <li>Everyone gets a turn to draw</li>
              <li>Most points at the end wins!</li>
            </ul>
          </details>
        </div>
      </div>
      </div>

      {/* Universal Sidebar with bottom-sheet mode on mobile */}
      <GameRoomSidebar
        mode="network"
        gameState={{
          turn: gameState.phase === 'drawing' ? `Player ${gameState.currentDrawer} drawing` : 'Waiting',
          phase: gameState.phase === 'finished' ? 'Game Over' : 'Playing',
          score: sortedScores.length > 0 ? Object.fromEntries(
            sortedScores.map(([id, score]) => [`Player ${id}`, `${score} pts`])
          ) : {}
        }}
        messages={chatMessages}
        historyItems={historyItems}
        onSendMessage={handleSidebarMessage}
        collapsed={sidebar.collapsed}
        onToggleCollapse={sidebar.toggle}
        position={sidebar.position}
        quickReactions={true}
      />
    </div>
  )
}
